// ============================================================
// CHART COLOR THEME — Single source of truth for all charts
// Change colors HERE and every chart across the app updates.
// ============================================================

// Figma design system palette — priority order left-to-right from Figma
// First 5 are PRIMARY chart colors, 6-11 are secondary
export const CHART_PALETTE = {
  purple:      '#8A3FFC',  // Priority 1
  blue:        '#3399FF',  // Priority 2
  green:       '#2ECC71',  // Priority 3
  coral:       '#FF6B3D',  // Priority 4
  lightPurple: '#A678FF',  // Priority 5
  golden:      '#E9B33F',  // Priority 6
  magenta:     '#C253C4',  // Priority 7
  cyan:        '#00C2FF',  // Priority 8
  hotPink:     '#FF4DA6',  // Priority 9
  teal:        '#22D3A6',  // Priority 10
  red:         '#BF4242',  // Priority 11
};

// Ordered sequence — used when assigning colors to series/slices (priority order)
export const CHART_COLOR_SEQUENCE = [
  CHART_PALETTE.purple,
  CHART_PALETTE.blue,
  CHART_PALETTE.green,
  CHART_PALETTE.coral,
  CHART_PALETTE.lightPurple,
  CHART_PALETTE.golden,
  CHART_PALETTE.magenta,
  CHART_PALETTE.cyan,
  CHART_PALETTE.hotPink,
  CHART_PALETTE.teal,
  CHART_PALETTE.red,
];

// Per-chart-type palettes (subsets/orderings of the master palette, same priority)
export const CHART_TYPE_COLORS = {
  pie:     [...CHART_COLOR_SEQUENCE],
  donut:   [...CHART_COLOR_SEQUENCE],
  bar:     [...CHART_COLOR_SEQUENCE],
  line:    [...CHART_COLOR_SEQUENCE],
  area:    [...CHART_COLOR_SEQUENCE],
  stacked: [...CHART_COLOR_SEQUENCE],
  heatmap: { low: '#F3F4F6', mid: CHART_PALETTE.lightPurple, high: CHART_PALETTE.purple },
  radar:   { low: CHART_PALETTE.lightPurple, high: CHART_PALETTE.purple },
};

// Semantic colors (gauges, backgrounds, grids)
// Positive = green, Negative = red, Neutral = blue
export const SEMANTIC_COLORS = {
  positive: '#2ECC71',        // Green
  negative: '#BF4242',        // Red
  neutral: '#3399FF',         // Blue
  background: '#E8E8E8',
  backgroundLight: '#F6F7F7',
  grid: '#E5E7EB',
};

// Gradient presets for area/line charts
export const CHART_GRADIENTS = {
  primaryToTertiary: { start: CHART_PALETTE.purple, mid: CHART_PALETTE.blue, end: CHART_PALETTE.lightPurple },
  secondaryToAccent3: { start: CHART_PALETTE.coral, mid: CHART_PALETTE.green, end: CHART_PALETTE.teal },
};

// ============================================================
// AUTO-SHADE GENERATOR
// Produces unique lighter/darker variants of base colors.
// Uses HSL hue-shifting + lightness variation to handle 1000s
// of data points without repeating or converging to white/black.
// ============================================================

const hex2rgb = (hex) => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
];

const rgb2hex = (r, g, b) =>
  '#' +
  [r, g, b]
    .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0'))
    .join('');

const rgb2hsl = (r, g, b) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return [h * 360, s * 100, l * 100];
};

const hsl2rgb = (h, s, l) => {
  h /= 360; s /= 100; l /= 100;
  if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hue2rgb(p, q, h + 1/3) * 255),
    Math.round(hue2rgb(p, q, h) * 255),
    Math.round(hue2rgb(p, q, h - 1/3) * 255),
  ];
};

export const generateShades = (baseColors, count) => {
  if (count <= baseColors.length) return baseColors.slice(0, count);
  const result = [...baseColors];

  // Convert base colors to HSL for smarter variation
  const baseHSL = baseColors.map((hex) => rgb2hsl(...hex2rgb(hex)));

  // Strategy: cycle through base colors, applying progressively different
  // hue shifts and lightness adjustments. Each "ring" applies a unique
  // combination so colors stay visually distinct even at 1000+ items.
  const lightnessSteps = [10, -12, 20, -20, 30, -28, 5, -8, 15, -15, 25, -25];
  const hueShifts = [0, 15, -15, 30, -30, 8, -8, 22, -22, 45, -45, 60];

  let ring = 0;
  while (result.length < count) {
    const lStep = lightnessSteps[ring % lightnessSteps.length];
    const hShift = hueShifts[Math.floor(ring / lightnessSteps.length) % hueShifts.length];
    // Add a small saturation tweak per ring to further separate colors
    const sTweak = (ring % 3 === 1) ? -8 : (ring % 3 === 2) ? 8 : 0;

    for (let i = 0; i < baseColors.length && result.length < count; i++) {
      const [h, s, l] = baseHSL[i];
      const newH = ((h + hShift) % 360 + 360) % 360;
      const newS = Math.max(15, Math.min(100, s + sTweak));
      const newL = Math.max(20, Math.min(85, l + lStep));
      const [r, g, b] = hsl2rgb(newH, newS, newL);
      result.push(rgb2hex(r, g, b));
    }
    ring++;
  }
  return result;
};

// Convenience: get N colors for a given chart type, auto-extending if needed
export const getChartColors = (type, count) => {
  const base = CHART_TYPE_COLORS[type] || CHART_COLOR_SEQUENCE;
  // For object-type palettes (heatmap, radar), return the sequence
  if (!Array.isArray(base)) return generateShades(CHART_COLOR_SEQUENCE, count);
  return generateShades(base, count);
};

// Convert hex to rgba string
export const hexToRgba = (hex, alpha) => {
  const [r, g, b] = hex2rgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
